function replaceName(text, firstName) {
    const name = firstName || 'there';
    const replaced = String(text || '')
        .replace(/\[Name\]/gi, name)
        .replace(/\{\{firstName\}\}/gi, name);
    const lines = replaced.replace(/\r/g, '').split('\n');
    if (lines.length && /^(?:Hello|Hi)\s*,?\s*$/i.test(lines[0])) {
        const greeting = /^Hello/i.test(lines[0]) ? 'Hello' : 'Hi';
        lines[0] = `${greeting} ${name},`;
    }
    return lines.join('\n');
}
const caseStudyMarker = /I(?:’|')ve picked out some case studies[^\n]*:?/i;
const caseStudyPlaceholder = /^\s*\[Case studies\]\s*$/i;
function looksLikeLegacyCaseStudyBlock(lines) {
    if (lines.length < 2)
        return false;
    return lines.every(raw => {
        const line = raw.trim();
        if (!line || line.length > 140)
            return false;
        if (/[.!?]$/.test(line))
            return false;
        if (/^(?:I|We|Our|If|Let|Many|Thanks|Thank|Kind|Best|Regards|Please|You|For|No|Happy|Appreciate)\b/i.test(line))
            return false;
        return true;
    });
}
function replaceCaseStudyPlaceholder(body, caseNames) {
    const lines = body.replace(/\r/g, '').split('\n');
    let handled = false;
    const output = [];
    for (const line of lines) {
        if (caseStudyPlaceholder.test(line)) {
            handled = true;
            output.push(...caseNames);
        }
        else {
            output.push(line);
        }
    }
    return { body: output.join('\n'), handled };
}
function injectSelectedCaseStudies(body, caseNames) {
    if (!caseNames.length)
        return body;
    const lines = body.replace(/\r/g, '').split('\n');
    const markerIndex = lines.findIndex(line => caseStudyMarker.test(line));
    if (markerIndex < 0) {
        return `${body.trim()}\n\nI’ve picked out a few case studies which I think may be relevant:\n${caseNames.join('\n')}`;
    }
    let cursor = markerIndex + 1;
    while (cursor < lines.length && !lines[cursor].trim())
        cursor++;
    const blockStart = cursor;
    while (cursor < lines.length && lines[cursor].trim())
        cursor++;
    const candidate = lines.slice(blockStart, cursor);
    const tailStart = looksLikeLegacyCaseStudyBlock(candidate) ? cursor : markerIndex + 1;
    const tail = lines.slice(tailStart);
    while (tail.length && !tail[0].trim())
        tail.shift();
    return [
        ...lines.slice(0, markerIndex + 1),
        ...caseNames,
        '',
        ...tail
    ].join('\n');
}
function applyOutreachUrl(body, outreachUrl) {
    const custom = String(outreachUrl || '').trim();
    if (!custom)
        return body;
    return body
        .replace(/https?:\/\/(?:www\.)?baileymce\.com\/proposal\/welcome/gi, custom)
        .replace(/baileymce\.com\/proposal\/welcome/gi, custom.replace(/^https?:\/\/(?:www\.)?/i, ''));
}
export function composeEmail(input) {
    const { template, firstName, personalisation = '', caseStudies = [], outreachUrl } = input;
    let body = replaceName(template.body, firstName);
    if (/\[Personalisation paragraph\]/i.test(body)) {
        body = body.replace(/\[Personalisation paragraph\]/gi, personalisation.trim());
    }
    else if (personalisation.trim()) {
        const parts = body.split(/\n\n/);
        const insertAt = Math.min(parts.length, 2);
        parts.splice(insertAt, 0, personalisation.trim());
        body = parts.join('\n\n');
    }
    const caseNames = caseStudies.map(x => x.name.trim()).filter(Boolean);
    const casePlacement = replaceCaseStudyPlaceholder(body, caseNames);
    body = casePlacement.body;
    // New scripts can place proof explicitly with [Case studies]. Legacy scripts keep
    // the existing sentence-based insertion behaviour until they are edited.
    if (!casePlacement.handled && caseNames.length)
        body = injectSelectedCaseStudies(body, caseNames);
    body = applyOutreachUrl(body, outreachUrl);
    return {
        subject: template.subject,
        body: body.replace(/\n{3,}/g, '\n\n').trim(),
        scriptKey: template.key,
    };
}
function escapeHtml(value) {
    return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
export function signatureTextFromHtml(html) {
    return String(html || '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/div>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&#39;/gi, "'")
        .replace(/&quot;/gi, '"')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}
export function appendPlainSignature(body, signatureHtml) {
    const signature = signatureTextFromHtml(signatureHtml);
    if (!signature)
        return String(body || '').trimEnd();
    return `${String(body || '').trimEnd()}\n\n${signature}`;
}
export function renderEmailHtml(plainBody, caseStudies = [], signatureHtml = '', caseStudyUrlOverrides = {}) {
    const linkByName = new Map(caseStudies
        .map(item => [item.name.trim().toLowerCase(), String(caseStudyUrlOverrides[item.id] || item.url || '').trim()])
        .filter(([name, url]) => !!name && /^https?:\/\//i.test(url)));
    const lines = String(plainBody || '').replace(/\r/g, '').split('\n');
    let html = '';
    let inCaseRun = false;
    for (const raw of lines) {
        const line = String(raw || '').trim();
        if (!line) {
            if (inCaseRun) {
                html += '</div>';
                inCaseRun = false;
            }
            html += '<div><br></div>';
            continue;
        }
        const url = linkByName.get(line.toLowerCase());
        if (url) {
            if (!inCaseRun) {
                html += '<div>';
                inCaseRun = true;
            }
            else
                html += '<br>';
            html += `<a href="${escapeHtml(url)}">${escapeHtml(line)}</a>`;
            continue;
        }
        if (inCaseRun) {
            html += '</div>';
            inCaseRun = false;
        }
        html += `<div>${escapeHtml(line)}</div>`;
    }
    if (inCaseRun)
        html += '</div>';
    const signature = String(signatureHtml || '').trim();
    if (signature)
        html += '<div><br></div>' + signature;
    return html;
}
export function composeFollowUp(input) {
    const { template, firstName, previousSubject, opportunityName } = input;
    // Follow-up copy is owned by Settings. Only documented placeholders are resolved here;
    // do not inject opportunity or case-study sentences outside the saved script.
    const body = replaceName(template.body, firstName)
        .replace(/\{\{previousSubject\}\}/gi, previousSubject)
        .replace(/\[Previous subject\]/gi, previousSubject)
        .replace(/\[Opportunity\]/gi, opportunityName || '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
    const subject = template.subject
        .replace(/\{\{previousSubject\}\}/gi, previousSubject)
        .replace(/\[Previous subject\]/gi, previousSubject)
        .replace(/^Re:\s*Re:\s*/i, 'Re: ')
        .trim();
    return { subject, body, scriptKey: template.key };
}
